import { REGISTER_LABEL, humanizeAxisKey } from '../components/AxisLensCard';
import type { JobRecord } from './api';

/**
 * Pure bucket/filter/sort + display-formatting logic for the Jobs (Discover)
 * tab — no fetch, no React, no DOM. Mirrors corpusGrouping.ts's shape (plain
 * function-in/value-out, unit-testable standalone, house idiom: no RTL render
 * tests anywhere in this codebase). The SIX buckets (LANE W2.1-BUCKETS,
 * extending W1-4; jobs.routes.ts's BUCKET_RANK) are read here exactly as the
 * backend assigns them — this module never re-derives a bucket from job
 * facts, it only groups/labels/sorts what the server already decided
 * (no-frontend-bandaids law: bucketing is a backend concern, this is
 * presentation only).
 *
 * COUNTRY-AGNOSTIC (Jason's direct ruling): every label/note below describes
 * the DERIVED rule the backend actually enforces — "your home country,"
 * "outside your home country" — never a specific country name. The old
 * 'bridge' note said "Australia, with a sponsorship signal"; that string is
 * gone, because bridge means ANY foreign country now (bucketJob.ts, R203).
 */

export type JobBucket = 'home' | 'relocate' | 'bridge' | 'dream' | 'pass' | 'unsorted';

/** Every bucket a job can carry, in the SAME promise order the backend's
 *  BUCKET_RANK sorts by (jobs.routes.ts) — home first ("no relocation
 *  stress"), pass last (the user's own explicit "not a fit," the most
 *  deliberately deprioritized state). A job with bucket=null (never scored
 *  under the bucket feature, or predating this column) is grouped under
 *  UNDETERMINED_BUCKET_KEY, sorted alongside 'unsorted' — visible, never
 *  hidden, exactly like the backend's `ELSE 4` rank. */
export const BUCKET_ORDER: JobBucket[] = ['home', 'relocate', 'bridge', 'dream', 'unsorted', 'pass'];

export const UNDETERMINED_BUCKET_KEY = 'undetermined' as const;

/** LANE W2.1-BUCKETS (Jason, direct: "I want a different color on each as
 *  well") — one DISTINCT theme token per bucket, no two buckets sharing a
 *  color. Defined for all three theme palettes (theme/themes.ts's
 *  ThemeTokens/THEMES/themeCssVars) so the legend swatch and the real
 *  per-card chip (JobCard.module.css's `[data-bucket=...]` selectors) always
 *  resolve the SAME hue — one source of truth. */
export type BucketColorVar =
  | '--tl-bucket-home'
  | '--tl-bucket-relocate'
  | '--tl-bucket-bridge'
  | '--tl-bucket-dream'
  | '--tl-bucket-pass'
  | '--tl-bucket-unsorted'
  // BUG-2026-07-08 PACKAGE E — its own distinct token, no longer a reuse of
  // --tl-bucket-unsorted (see BUCKET_CHIPS' UNDETERMINED_BUCKET_KEY entry).
  | '--tl-bucket-undetermined';

export interface BucketChipConfig {
  key: JobBucket | typeof UNDETERMINED_BUCKET_KEY;
  label: string;
  /** Short, honest description of what lands a job in this bucket — shown as
   *  a tooltip/subtitle, never a marketing line. */
  note: string;
  /** QA #17 (color-coding KEY) + LANE W2.1-BUCKETS (a DISTINCT color per
   *  bucket, Jason's direct ask). A named semantic color token, NOT a raw
   *  hex — resolved by the caller against theme/themes.ts's own CSS
   *  variables, the SAME ones JobCard.module.css's `[data-bucket=...]`
   *  selectors resolve, so the legend swatch and the actual per-card chip
   *  are guaranteed to match. 'undetermined' (bucket=null) reuses
   *  '--tl-bucket-unsorted' — a null bucket IS the unsorted state, never a
   *  fabricated seventh color for what is semantically the same thing. */
  colorVar: BucketColorVar;
}

/** Chip copy for the bucket filter row + the per-card bucket chip. Wording
 *  mirrors the backend's own bucketJob.ts doc comments and NEVER claims a
 *  rule the code doesn't enforce — home = same country as yours + nearby-or-
 *  remote, relocate = same country, not nearby, bridge = a DIFFERENT country
 *  + a live sponsorship signal (ANY foreign country — R203, never one named
 *  here), dream = a target company/title match, pass = the user's own
 *  explicit "not a current fit," unsorted = not yet confidently placed.
 *
 *  QA #17 (Jobs filters need human-centered design): these labels ("Home",
 *  "Relocate", "Bridge", "Dream", "Pass", "Unsorted") are the CURRENT
 *  best-effort plain-English gloss on the backend's six-bucket lane —
 *  already a mapping layer over the internal bucket KEYS, never a rename of
 *  the keys themselves. Jason's walkthrough flagged that even these read as
 *  internal metaphors to a first-time user ("Home"/"Bridge" don't
 *  self-explain without the `note` alongside them) — see this lane's build
 *  report for a written proposal on tightening the labels further; NEITHER
 *  is changed in this pass beyond the honest-rule rewrite above (scope:
 *  labels + a distinct color per bucket, never the bucketing logic that
 *  assigns the key). */
export const BUCKET_CHIPS: BucketChipConfig[] = [
  {
    key: 'home',
    label: 'Nearby',
    note: 'In your home country, near a declared location or fully remote — no relocation stress',
    colorVar: '--tl-bucket-home',
  },
  {
    key: 'relocate',
    label: 'Relocate',
    note: 'In your home country, but not near a declared location — a domestic move, no visa involved',
    colorVar: '--tl-bucket-relocate',
  },
  {
    key: 'bridge',
    label: 'Overseas',
    note: 'Outside your home country, with a sponsorship signal',
    colorVar: '--tl-bucket-bridge',
  },
  {
    key: 'dream',
    label: 'Dream',
    note: 'A target company or one of your declared titles',
    colorVar: '--tl-bucket-dream',
  },
  {
    key: 'pass',
    label: 'Pass',
    note: 'Kept for the record — not a current fit',
    colorVar: '--tl-bucket-pass',
  },
  {
    key: 'unsorted',
    label: 'Unsorted',
    note: 'Looked at and not yet confidently placed — yours to sort',
    colorVar: '--tl-bucket-unsorted',
  },
  {
    // BUG-2026-07-08 PACKAGE E (his words: "two unsorted tabs with no
    // distinction of what they are and return different results.
    // Unacceptable."). bucket='unsorted' (explicitly triaged as unsorted by
    // the auto-scorer, bucketJob.ts) and bucket=NULL (never scored at all —
    // e.g. predates the bucket feature, or scoring hasn't run yet) are
    // DISTINCT states with a real, honest difference: one has been looked at
    // and the scorer genuinely couldn't place it; the other simply hasn't
    // been examined yet. Two identical "Unsorted" labels hid that
    // distinction and returned different counts under the same name — never
    // acceptable. "New" reads correctly in the Friend voice (plain, no
    // internal-metaphor baggage) and is honestly true: a null-bucket job IS
    // new to the system's own triage. Distinct color token too (was a
    // deliberate reuse of --tl-bucket-unsorted before this fix, per the old
    // comment on this literal — now genuinely its own token; see
    // theme/themes.ts's bucketUndetermined addition).
    key: UNDETERMINED_BUCKET_KEY,
    label: 'New',
    note: 'Not yet scored — bucket not determined',
    colorVar: '--tl-bucket-undetermined',
  },
];

export function bucketChipLabel(bucket: JobBucket | null): string {
  const key = bucket ?? UNDETERMINED_BUCKET_KEY;
  return BUCKET_CHIPS.find((c) => c.key === key)?.label ?? key;
}

/** LANE W2.1-BUCKETS — the user-sort bucket PICKER's option list: every real
 *  bucket a person can explicitly choose, INCLUDING 'pass' (the one value
 *  the auto-scorer itself can never produce — see bucketJob.ts's
 *  AutoJobBucket). Excludes the synthetic UNDETERMINED_BUCKET_KEY (that key
 *  exists only for a null/never-scored row's DISPLAY grouping — a user
 *  overriding a bucket always picks a REAL value, 'unsorted' included, never
 *  the legacy-null placeholder). */
export const BUCKET_PICKER_OPTIONS: { value: JobBucket; label: string; note: string }[] = BUCKET_CHIPS.filter(
  (c): c is BucketChipConfig & { key: JobBucket } => c.key !== UNDETERMINED_BUCKET_KEY,
).map((c) => ({ value: c.key, label: c.label, note: c.note }));

/** QA #34 — the honest "in flight" chip for a tracked/applied job.
 *  tracking_status is the applications table's own ApplicationStatus
 *  ('wishlist'|'applied'|'screen'|'onsite'|'offer'|'closed'), or undefined/
 *  null when the user has never tracked this job — returns null in that case
 *  (never a fabricated "not applied" chip; JobCard simply omits the chip).
 *  Labels mirror the Tracker board's own stage names (apps/web's Tracker.tsx
 *  precedent, APPLICATION_STATUSES) — never invents a stage the board
 *  doesn't have. */
export function trackingStatusLabel(
  status: JobRecord['tracking_status'] | undefined,
): string | null {
  if (!status) return null;
  switch (status) {
    case 'wishlist':
      return 'On your tracking board';
    case 'applied':
      return 'Applied';
    case 'screen':
      return 'In screening';
    case 'onsite':
      return 'Onsite';
    case 'offer':
      return 'Offer';
    case 'closed':
      return 'Closed';
    default:
      return null;
  }
}

/** The canonical grouping/display order: BUCKET_ORDER's real six values, but
 *  with UNDETERMINED_BUCKET_KEY (a null/never-scored bucket) spliced in
 *  RIGHT BESIDE 'unsorted' — semantically the same "needs sorting" state,
 *  never sorted after 'pass' (the user's own deliberate deprioritization,
 *  which stays the true last-place bucket per BUCKET_RANK). */
const GROUP_DISPLAY_ORDER: (JobBucket | typeof UNDETERMINED_BUCKET_KEY)[] = [
  'home',
  'relocate',
  'bridge',
  'dream',
  'unsorted',
  UNDETERMINED_BUCKET_KEY,
  'pass',
];

/** Group jobs by bucket, in GROUP_DISPLAY_ORDER — the SAME precedence
 *  sort='bucket' returns from the server (BUCKET_RANK), reproduced here for
 *  the client-side bucket FILTER chips (grouping is over an already-sorted
 *  list; this never re-ranks fit within a group, that ordering is the
 *  server's). A bucket with zero jobs is OMITTED from the result (nothing to
 *  show), but the input list itself is never filtered/dropped — every job
 *  the server sent stays reachable in some group. */
export function groupJobsByBucket(jobs: JobRecord[]): Map<JobBucket | typeof UNDETERMINED_BUCKET_KEY, JobRecord[]> {
  const groups = new Map<JobBucket | typeof UNDETERMINED_BUCKET_KEY, JobRecord[]>();
  for (const job of jobs) {
    const key = job.bucket ?? UNDETERMINED_BUCKET_KEY;
    const arr = groups.get(key) ?? [];
    arr.push(job);
    groups.set(key, arr);
  }
  // Re-insert in canonical order so Map iteration (and Array.from(...)) yields
  // home, relocate, bridge, dream, unsorted, undetermined, pass — never
  // insertion order.
  const ordered = new Map<JobBucket | typeof UNDETERMINED_BUCKET_KEY, JobRecord[]>();
  for (const key of GROUP_DISPLAY_ORDER) {
    const arr = groups.get(key);
    if (arr?.length) ordered.set(key, arr);
  }
  return ordered;
}

/** Filter jobs to a single bucket (or 'all'). Pure — returns a NEW array,
 *  never mutates. 'all' is the identity filter (every job the server sent). */
export function filterJobsByBucket(
  jobs: JobRecord[],
  bucket: JobBucket | typeof UNDETERMINED_BUCKET_KEY | 'all',
): JobRecord[] {
  if (bucket === 'all') return [...jobs];
  return jobs.filter((j) => (j.bucket ?? UNDETERMINED_BUCKET_KEY) === bucket);
}

/** Parse a raw numeric-as-string Postgres column (fit_score, sponsor_likelihood)
 *  into a number, or null. Mirrors apps/web/src/pages/Inbox.tsx's FitChip
 *  parse (`job.fit_score ? Number.parseFloat(job.fit_score) : null`) — the
 *  proven pattern for this codebase's raw-SQL numeric columns. An empty
 *  string or a non-numeric value also reads as null (NaN-safe), never a
 *  fabricated 0. */
export function parseNumericColumn(raw: string | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  const n = Number.parseFloat(raw);
  return Number.isNaN(n) ? null : n;
}

/** fit_score is 0-100 already (job_scores.fitScore, numeric(5,2)) — round for
 *  display, or null when undetermined. Never clamps/defaults a real value. */
export function jobFitScore(job: Pick<JobRecord, 'fit_score'>): number | null {
  const n = parseNumericColumn(job.fit_score);
  return n === null ? null : Math.round(n);
}

/** sponsor_likelihood is a 0-1 probability (job_scores.sponsorLikelihood,
 *  numeric(4,3)) — returned as a 0-100 rounded percentage for display, or
 *  null when the job has never been scored. Distinct from
 *  sponsorship_mentioned (a boolean JD-text scan at extraction time, not a
 *  probability) — callers that want the honest sponsorship READ should
 *  prefer this when present, and fall back to sponsorship_mentioned only
 *  when the job has no score yet (see sponsorshipReadLabel). */
export function sponsorLikelihoodPct(job: Pick<JobRecord, 'sponsor_likelihood'>): number | null {
  const n = parseNumericColumn(job.sponsor_likelihood);
  return n === null ? null : Math.round(n * 100);
}

/**
 * The honest "sponsorship read when present" the brief asks for. Combines
 * the two REAL signals this codebase has — sponsor_likelihood (a scored
 * probability) and sponsorship_mentioned (a boolean JD-text scan) — and
 * returns EXACTLY ONE of: a percentage line, a boolean-only line, or null
 * (genuinely nothing to show). Never infers a third value neither field
 * states (there is no served categorical explicit_yes/explicit_no/silent
 * signal on this row shape — scanSponsorshipSignal is computed server-side
 * for bucketing only and is not returned by GET /jobs or POST /jobs/search;
 * see api.ts's JOBS module header). Prefers the scored likelihood (it is the
 * more informative of the two) when both are present.
 */
export function sponsorshipReadLabel(
  job: Pick<JobRecord, 'sponsor_likelihood' | 'sponsorship_mentioned'>,
): string | null {
  const pct = sponsorLikelihoodPct(job);
  if (pct !== null) return `Sponsor likelihood ${pct}%`;
  if (job.sponsorship_mentioned === true) return 'Sponsorship mentioned';
  if (job.sponsorship_mentioned === false) return 'No sponsorship mentioned';
  return null;
}

/** AXIS-VIZ (R299 follow-up) — the Jobs-list "glance chip" label: "Reads:
 *  Leadership · senior". NO INVENTION: both `top_axis_key` AND
 *  `axis_register` must be present (the joined listing_axis_reads row
 *  existed AND traced at least one axis — see topAxisGlance's own
 *  all-or-nothing contract, AxisLensService.ts) or this returns null and
 *  JobCard renders NO chip at all — never a chip built from just one of the
 *  two fields, never a guessed label while a read doesn't exist for this
 *  listing yet. Reuses AxisLensCard's own humanize/register-label helpers so
 *  a job's list-row chip and its full detail-page read speak the identical
 *  display vocabulary (same lens concept, one word choice). */
export function axisGlanceLabel(
  job: Pick<JobRecord, 'top_axis_key' | 'axis_register'>,
): string | null {
  if (!job.top_axis_key || !job.axis_register) return null;
  const registerLabel = REGISTER_LABEL[job.axis_register] ?? job.axis_register;
  return `Reads: ${humanizeAxisKey(job.top_axis_key)} · ${registerLabel.toLowerCase()}`;
}

/**
 * BOARD-TRUTH FIX (owner report: a multi-location "Nearby" card showed
 * "Ontario, CAN" — a FOREIGN city — as its face, because the card always
 * rendered the raw, potentially foreign-city-leading `location` string
 * regardless of bucket). Prefers the SERVED resolved_location — the specific
 * segment that actually justified a 'home'/'relocate' bucket
 * (bucketJob.ts's BucketDecision.matchedLocation) — over the raw multi-
 * location string, but ONLY for those two buckets (bridge/dream/unsorted/pass
 * make no "you're near this" promise, so the raw string is the honest,
 * correct thing to show there — never swapped out). Falls back to the raw
 * `location` (still run through orderLocationForUS's own US-first display
 * reorder, unchanged) whenever resolved_location is null — a single-location
 * job, a pre-fix row that hasn't been rescored yet, or a bucket this fix
 * doesn't touch. This is a PURE display preference — it never re-buckets or
 * re-scores; the server already decided which segment mattered, this only
 * chooses which of two already-served strings to show.
 */
export function displayLocationForCard(
  job: Pick<JobRecord, 'bucket' | 'resolved_location'>,
  orderedRawLocation: string | null,
): string | null {
  const bucketNeedsResolve = job.bucket === 'home' || job.bucket === 'relocate';
  if (bucketNeedsResolve && job.resolved_location) return job.resolved_location;
  return orderedRawLocation;
}

/** "as of" freshness label (W1-11e: "recency is a fact; unstated freshness is
 *  invented freshness"). Renders the SERVED staleMinutes honestly — never
 *  re-derives a duration from asOf + a client clock (a client's wall clock
 *  can be skewed; the server already did this arithmetic against its own
 *  resolved temporal clock — see apps/api/lib/asOf.ts). null in, null out. */
export function freshnessLabel(job: Pick<JobRecord, 'asOf' | 'staleMinutes'>): string | null {
  if (job.asOf == null || job.staleMinutes == null) return null;
  const m = job.staleMinutes;
  if (m < 1) return 'as of moments ago';
  if (m < 60) return `as of ${m} min ago`;
  const hours = Math.floor(m / 60);
  if (hours < 24) return `as of ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `as of ${days}d ago`;
}

/** W21 (docs/WALK-FINDINGS-2026-07-21.md) — the honest "may no longer be
 *  advertised" notice, the staleness-honesty half of the provenance-
 *  integrity lane ("The first job was linked to a teacher position" — a
 *  listing not re-seen in weeks presented a confident, unqualified outbound
 *  link). Renders ONLY when the SERVED isStale verdict is true — this
 *  function never re-derives the threshold client-side (no-frontend-
 *  bandaids: the threshold is a business decision, decided once, server-side
 *  — see apps/api/lib/asOf.ts's STALE_LISTING_THRESHOLD_MINUTES for the full
 *  reasoning). null whenever isStale is false OR the freshness signal is
 *  genuinely unknown (isStale: null, mirroring freshnessLabel's own
 *  null-in/null-out contract). NEVER asserts the listing is dead — only
 *  that it hasn't been re-seen since the real, named asOf date (NO
 *  INVENTION: silence about WHY is honest; a claim of death would not be).
 *  Date formatted en-US (CLAUDE.md's default locale), matching this
 *  codebase's own toLocaleDateString('en-US') convention (Settings.tsx). */
export function staleListingNotice(
  job: Pick<JobRecord, 'asOf' | 'staleMinutes' | 'isStale'>,
): string | null {
  if (job.isStale !== true || job.asOf == null) return null;
  const lastSeen = new Date(job.asOf).toLocaleDateString('en-US');
  return `Last seen ${lastSeen} — may no longer be advertised`;
}

/** Sort order labels for the sort control — mirrors the exact `sort` query
 *  values GET /jobs accepts (jobs.routes.ts:1539: 'fit' | 'recent' | 'bucket'),
 *  so the FE can never send a value the backend doesn't recognise. */
export const JOB_SORT_OPTIONS: { value: 'fit' | 'recent' | 'bucket'; label: string }[] = [
  { value: 'fit', label: 'Best fit' },
  { value: 'recent', label: 'Most recent' },
  { value: 'bucket', label: 'By bucket' },
];

/** QA #26 — JobDetail's prominent "Apply on {source}" link. Every entry here
 *  is a REAL hostname this codebase actually ingests from (verified against
 *  apps/api/lib/atsAutoDiscovery.ts's greenhouse/lever/ashby/smartrecruiters/
 *  workday hosts, and the 'adzuna'/'dice' AtsKind-adjacent sources
 *  WatchlistPollingService.ts polls) — this is display-only friendliness
 *  ("Greenhouse" reads better than "job-boards.greenhouse.io"), never a claim
 *  about which ATS actually powers a listing beyond what its own URL states.
 *  An unrecognized host falls back to the honest, generic "company site"
 *  rather than fabricating a vendor name. */
const HOST_SOURCE_LABELS: { pattern: RegExp; label: string }[] = [
  { pattern: /(^|\.)greenhouse\.io$/i, label: 'Greenhouse' },
  { pattern: /(^|\.)lever\.co$/i, label: 'Lever' },
  { pattern: /(^|\.)myworkdayjobs\.com$/i, label: 'Workday' },
  { pattern: /(^|\.)workday\.com$/i, label: 'Workday' },
  { pattern: /(^|\.)dice\.com$/i, label: 'Dice' },
  // developer.adzuna.com/docs/search's own documented redirect_url example
  // is host `adzuna.co.uk` (confirmed, no www.) — the `.com`/`.com.au`
  // variants are included defensively for other country codes the same
  // aggregator endpoint accepts (au/us/etc — WatchlistPollingService.ts's
  // pollAdzuna), since Adzuna's per-country redirect host was not otherwise
  // confirmable without a live authenticated call.
  { pattern: /(^|\.)adzuna\.(com(\.[a-z]{2})?|co\.[a-z]{2})$/i, label: 'Adzuna' },
  { pattern: /(^|\.)ashbyhq\.com$/i, label: 'Ashby' },
  { pattern: /(^|\.)smartrecruiters\.com$/i, label: 'SmartRecruiters' },
];

/**
 * Derive a friendly "apply on {source}" label from a job's canonical_url
 * host. Returns 'company site' (never null, never a fabricated vendor name)
 * for any host not in the recognized list, an unparsable URL, or a
 * null/empty input — the caller always has honest copy to render even when
 * the URL is odd. Pure string logic; does not fetch, does not validate the
 * URL is reachable.
 */
export function applySourceLabel(canonicalUrl: string | null | undefined): string {
  if (!canonicalUrl) return 'company site';
  let host: string;
  try {
    host = new URL(canonicalUrl).hostname;
  } catch {
    return 'company site';
  }
  const match = HOST_SOURCE_LABELS.find((h) => h.pattern.test(host));
  return match?.label ?? 'company site';
}

// =============================================================================
// CLASS 10 — THE RESCORING ENGINE (D21). Jason's 03:01 EDT refinement: "It
// should say 'I DO know — this is what I know SO FAR' — and maybe not give a
// straight number yet." score_confidence/score_known_facets/score_pending_facets
// are SERVED (jobsLakeReads.ts's withScoreConfidence) — this module only
// formats them, never re-derives the tri-state or the facet set client-side.
// =============================================================================

/** Plain-English gloss per named facet key — mirrors bucketChipLabel's own
 *  "internal key -> honest human label" mapping discipline. Never invents a
 *  facet the server didn't name. */
const FACET_LABELS: Record<string, string> = {
  jd_title_read: 'the role title',
  jd_comp_band: 'a pay range',
  jd_location_read: 'where it is',
  corpus_depth: 'your background',
  declared_home_base: 'your home base',
  declared_direction: 'your direction',
};

function facetLabel(key: string): string {
  return FACET_LABELS[key] ?? key.replace(/_/g, ' ');
}

/** The honest chip's copy for a job whose score_confidence is 'provisional'
 *  or 'not_yet_attempted' — "I know so far: X, Y — still need: Z" instead of
 *  a bare undetermined dash. Returns null for 'scored' (the ScoreRing
 *  renders instead) or when the server didn't serve score_confidence at all
 *  (POST /jobs/search rows this pass — see JobRecord's own doc comment),
 *  never a guessed chip. */
export function scoreConfidenceChipLabel(
  job: Pick<JobRecord, 'score_confidence' | 'score_known_facets' | 'score_pending_facets'>,
): string | null {
  if (job.score_confidence == null || job.score_confidence === 'scored') return null;
  if (job.score_confidence === 'not_yet_attempted') return 'Not enough to score yet';
  const known = job.score_known_facets ?? [];
  if (known.length === 0) return 'Not enough to score yet';
  const knownLabels = known.map(facetLabel).join(', ');
  return `I know so far: ${knownLabels}`;
}

/** CLASS 10 (D21), thread 3 — "it could go into a different bucket of ones
 *  to EXPLORE, not fully scored." A job whose score_confidence is
 *  'provisional' or 'not_yet_attempted' belongs in the honest "Explore" shelf
 *  — a SEPARATE dimension from the six-bucket lane (a job can be bucket=
 *  'home' AND still provisional; the two facts are independent), so this is
 *  its own filter predicate layered alongside filterJobsByBucket, never a
 *  seventh bucket value. Pure. */
export function isNotFullyScored(job: Pick<JobRecord, 'score_confidence'>): boolean {
  return job.score_confidence === 'provisional' || job.score_confidence === 'not_yet_attempted';
}

/** Split a job list into the "fully scored or bucket-decided" main flow vs.
 *  the honest "Explore — not fully scored" shelf. Pure — returns two NEW
 *  arrays, never mutates, never drops a job (every job the server sent stays
 *  reachable in exactly one of the two lists — the no-deletion law extended
 *  to this new dimension the same way GROUP_DISPLAY_ORDER already extends it
 *  for buckets). Jobs with score_confidence undefined (POST /jobs/search
 *  rows, no floor served) are treated as "main flow" — the honest default
 *  when the signal simply wasn't served, never assumed unscored. */
export function splitExploreShelf<T extends Pick<JobRecord, 'score_confidence'>>(
  jobs: T[],
): { mainFlow: T[]; explore: T[] } {
  const mainFlow: T[] = [];
  const explore: T[] = [];
  for (const job of jobs) {
    if (isNotFullyScored(job)) explore.push(job);
    else mainFlow.push(job);
  }
  return { mainFlow, explore };
}
